import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

function formatFecha(fechaStr) {
  if (!fechaStr) return '';
  try {
    const fecha = new Date(fechaStr);
    return fecha.toLocaleDateString('es-CL', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch { return fechaStr; }
}

const DIAS_SEMANA = ['Lunes','Martes','Miércoles','Jueves','Viernes','Sábado','Domingo'];

// FRANJAS solo para UI — no se guardan en BD
const FRANJAS_HORAS = [
  { id: 'manana', label: 'Mañana', horas: ['07:00','08:00','09:00','10:00','11:00'] },
  { id: 'tarde',  label: 'Tarde',  horas: ['12:00','13:00','14:00','15:00','16:00','17:00'] },
  { id: 'noche',  label: 'Noche',  horas: ['18:00','19:00','20:00','21:00','22:00'] },
];

const SERVICIOS_TT = [
  { id: 'asado',     label: 'Asado completo'       },
  { id: 'cervezas',  label: 'Cervezas'             },
  { id: 'empanadas', label: 'Empanadas'            },
  { id: 'bebidas',   label: 'Bebidas sin alcohol'  },
  { id: 'local',     label: 'Espacio físico'       },
  { id: 'domicilio', label: 'Servicio a domicilio' },
  { id: 'once',      label: 'Once / Desayuno'      },
  { id: 'picoteo',   label: 'Picoteo / Snacks'     },
];

export default function PanelProveedor() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();

  const [perfil,          setPerfil]          = useState(null);
  const [desafios,        setDesafios]        = useState([]);
  const [disponibles,     setDisponibles]     = useState([]);  // partidos para tomar
  const [editando,        setEditando]        = useState(false);
  const [form,            setForm]            = useState({});
  const [cargando,        setCargando]        = useState(true);
  const [guardando,       setGuardando]       = useState(false);
  const [tomando,         setTomando]         = useState(null); // id del desafío que está tomando
  const [error,           setError]           = useState('');
  const [franjasAbiertas, setFranjasAbiertas] = useState([]);

  const API = process.env.REACT_APP_API_URL;
  const rol  = user?.rol;

  // Abre Google Maps con la dirección del recinto
  const abrirMapa = (direccion, ciudad, nombre) => {
    if (!direccion) return;
    const partes = [nombre, direccion, ciudad].filter(Boolean).join(', ');
    const query  = encodeURIComponent(partes);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  useEffect(() => {
    if (!user?.email) return;
    setCargando(true);

    const endpoint = rol === 'arbitro'
      ? `${API}/api/arbitros/email/${user.email}`
      : `${API}/api/tercer-tiempo/email/${user.email}`;

    fetch(endpoint)
      .then(r => r.json())
      .then(async data => {
        if (data.error) { setCargando(false); return; }
        setPerfil(data);
        setForm(data);

        if (rol === 'arbitro' && data.id) {
          // Cargar desafíos asignados y partidos disponibles
          const cargarArbitro = async () => {
            const [resDesafios, resDisp] = await Promise.all([
              fetch(`${API}/api/arbitros/${data.id}/desafios`),
              fetch(`${API}/api/desafios/disponibles-arbitro/${data.id}`),
            ]);
            const desafiosData  = await resDesafios.json();
            const disponiblesData = await resDisp.json();
            if (Array.isArray(desafiosData))    setDesafios(desafiosData);
            if (Array.isArray(disponiblesData)) setDisponibles(disponiblesData);
          };
          await cargarArbitro();
          // Polling cada 30s para ambas listas
          const intervalo = setInterval(cargarArbitro, 30000);
          return () => clearInterval(intervalo);
        }
      })
      .catch(() => {})
      .finally(() => setCargando(false));
  }, [user]);

  const handleGuardar = async () => {
    if (!perfil?.id) return;
    setGuardando(true); setError('');
    try {
      const endpoint = rol === 'arbitro'
        ? `${API}/api/arbitros/${perfil.id}`
        : `${API}/api/tercer-tiempo/${perfil.id}`;

      const body = rol === 'arbitro' ? {
        zona:        form.zona,
        experiencia: form.experiencia,
        tarifa:      form.tarifa,
        certificado: form.certificado,
        federado:    form.federado,
        modalidades: form.modalidades,
        dias:        form.dias,
        horarios:    form.horarios,   // array de horas específicas ej: ['19:00','20:00']
      } : {
        negocio:     form.negocio,
        zona:        form.zona,
        descripcion: form.descripcion,
        tiene_local: form.tiene_local,
        a_domicilio: form.a_domicilio,
        instagram:   form.instagram,
        facebook:    form.facebook,
        tiktok:      form.tiktok,
        whatsapp:    form.whatsapp,
      };

      const res  = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.error) { setError(data.error); return; }
      setPerfil(data);
      setEditando(false);
      setFranjasAbiertas([]);
    } catch { setError('Error de conexión'); }
    finally  { setGuardando(false); }
  };

  const handleTomarPartido = async (desafioId) => {
    if (!perfil?.id) return;
    setTomando(desafioId);
    try {
      const res = await fetch(`${API}/api/desafios/${desafioId}/tomar-arbitraje`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ arbitro_id: perfil.id }),
      });
      const data = await res.json();
      if (data.error) {
        alert(data.error); // otro árbitro se adelantó
        // Refrescar disponibles
        const res2 = await fetch(`${API}/api/desafios/disponibles-arbitro/${perfil.id}`);
        const d2   = await res2.json();
        if (Array.isArray(d2)) setDisponibles(d2);
      } else {
        // Mover de disponibles a desafíos asignados
        setDisponibles(prev => prev.filter(d => d.id !== desafioId));
        setDesafios(prev => [...prev, data]);
      }
    } catch { alert('Error de conexión'); }
    finally  { setTomando(null); }
  };

  const toggleFranja = (id) => setFranjasAbiertas(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const toggleHora = (hora) => setForm(p => ({
    ...p,
    horarios: (p.horarios || []).includes(hora)
      ? (p.horarios || []).filter(x => x !== hora)
      : [...(p.horarios || []), hora]
  }));

  const pendientes = desafios.filter(d => d.estado === 'pendiente' || d.estado === 'aceptado');
  const jugados    = desafios.filter(d => d.estado === 'jugado');

  if (cargando) return (
    <div style={styles.pagina}>
      <div style={styles.cargando}>Cargando panel...</div>
    </div>
  );

  return (
    <div style={styles.pagina}>
      <div style={styles.container}>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.avatar}>
              {rol === 'arbitro' ? '🟡' : '🍺'}
            </div>
            <div>
              <div style={styles.nombre}>
                {rol === 'arbitro' ? perfil?.nombre : perfil?.negocio}
              </div>
              <div style={styles.rolBadge}>
                {rol === 'arbitro' ? 'Árbitro TuRival' : 'Tercer Tiempo TuRival'}
              </div>
              <div style={styles.email}>{user?.email}</div>
            </div>
          </div>
          <div style={styles.headerRight}>
            <button style={styles.btnEditar} onClick={() => { setEditando(!editando); setError(''); setFranjasAbiertas([]); }}>
              {editando ? 'Cancelar' : '✏️ Editar perfil'}
            </button>
            <button style={styles.btnSalir} onClick={() => { logout(); navigate('/login'); }}>
              Salir
            </button>
          </div>
        </div>

        {/* Notificaciones — desafíos pendientes */}
        {pendientes.length > 0 && (
          <div style={styles.notifBanner}>
            <span style={styles.notifIcono}>⚡</span>
            <div>
              <div style={styles.notifTitulo}>
                {pendientes.length === 1
                  ? '¡Tienes 1 partido asignado!'
                  : `¡Tienes ${pendientes.length} partidos asignados!`}
              </div>
              <div style={styles.notifSub}>
                Revisa los detalles abajo.
              </div>
            </div>
          </div>
        )}

        {error && <div style={styles.errorBanner}>{error}</div>}

        <div style={styles.cols}>
          <div style={styles.colLeft}>

            {/* Datos del perfil */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>
                {rol === 'arbitro' ? 'Mis datos de árbitro' : 'Mi negocio'}
              </div>

              {!editando ? (
                // ── VISTA ──
                rol === 'arbitro' ? (
                  <div>
                    {[
                      { l: 'Zona',        v: perfil?.zona },
                      { l: 'Experiencia', v: perfil?.experiencia ? `${perfil.experiencia} años` : '--' },
                      { l: 'Tarifa',      v: perfil?.tarifa ? `$${Number(perfil.tarifa).toLocaleString('es-CL')} / partido` : '--' },
                      { l: 'Certificado', v: perfil?.certificado ? '✓ Sí' : 'No' },
                      { l: 'Federado',    v: perfil?.federado    ? '✓ Sí' : 'No' },
                      { l: 'Modalidades', v: (perfil?.modalidades || []).join(', ') || '--' },
                    ].map(({ l, v }) => (
                      <div key={l} style={styles.infoRow}>
                        <span style={styles.infoLabel}>{l}</span>
                        <span style={styles.infoVal}>{v}</span>
                      </div>
                    ))}
                    {/* Disponibilidad */}
                    <div style={{ marginTop: 12 }}>
                      <div style={{ ...styles.editLabel, marginBottom: 8 }}>Disponibilidad</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                        {(perfil?.dias || []).map(d => (
                          <span key={d} style={styles.chipVista}>{d.slice(0,3)}</span>
                        ))}
                        {(perfil?.dias || []).length === 0 && <span style={styles.sinDatos}>Sin días definidos</span>}
                      </div>
                      {/* Mostrar horas específicas en vista */}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {(perfil?.horarios || []).sort().map(h => (
                          <span key={h} style={{ ...styles.chipVista, background: 'rgba(26,107,255,0.2)', color: '#60a5fa' }}>{h}</span>
                        ))}
                        {(perfil?.horarios || []).length === 0 && <span style={styles.sinDatos}>Sin horarios definidos</span>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    {[
                      { l: 'Negocio',     v: perfil?.negocio },
                      { l: 'Zona',        v: perfil?.zona },
                      { l: 'Descripción', v: perfil?.descripcion || '--' },
                      { l: 'Local',       v: perfil?.tiene_local ? '✓ Tiene local' : 'No' },
                      { l: 'Domicilio',   v: perfil?.a_domicilio ? '✓ A domicilio' : 'No' },
                      { l: 'WhatsApp',    v: perfil?.whatsapp    || '--' },
                      { l: 'Instagram',   v: perfil?.instagram   || '--' },
                    ].map(({ l, v }) => (
                      <div key={l} style={styles.infoRow}>
                        <span style={styles.infoLabel}>{l}</span>
                        <span style={styles.infoVal}>{v}</span>
                      </div>
                    ))}
                    {/* Servicios */}
                    {(perfil?.servicios || []).length > 0 && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ ...styles.editLabel, marginBottom: 8 }}>Servicios</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {perfil.servicios.map(s => {
                            const srv = SERVICIOS_TT.find(x => x.id === s);
                            return <span key={s} style={styles.chipVista}>{srv?.label || s}</span>;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )
              ) : (
                // ── EDICIÓN ──
                <div>
                  {rol === 'arbitro' ? (
                    <>
                      {[
                        { key: 'zona',        label: 'Zona',                    type: 'text'   },
                        { key: 'experiencia', label: 'Años de experiencia',     type: 'number' },
                        { key: 'tarifa',      label: 'Tarifa por partido ($)',  type: 'number' },
                      ].map(({ key, label, type }) => (
                        <div key={key} style={{ marginBottom: 12 }}>
                          <label style={styles.editLabel}>{label}</label>
                          <input
                            type={type}
                            value={form[key] || ''}
                            onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                            style={styles.editInput}
                          />
                        </div>
                      ))}

                      <div style={{ marginBottom: 12 }}>
                        <label style={styles.editLabel}>Certificado</label>
                        <div style={{ display: 'flex', gap: 10 }}>
                          {[true, false].map(v => (
                            <div key={String(v)} onClick={() => setForm(p => ({ ...p, certificado: v }))}
                              style={{ ...styles.toggleChip, background: form.certificado === v ? '#FF6B1A' : '#F4F6FA', color: form.certificado === v ? 'white' : '#4A5568' }}>
                              {v ? 'Sí' : 'No'}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div style={{ marginBottom: 12 }}>
                        <label style={styles.editLabel}>Días disponibles</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {DIAS_SEMANA.map(d => {
                            const activo = (form.dias || []).includes(d);
                            return (
                              <div key={d} onClick={() => setForm(p => ({
                                ...p,
                                dias: activo ? (p.dias || []).filter(x => x !== d) : [...(p.dias || []), d]
                              }))}
                              style={{ ...styles.toggleChip, background: activo ? '#1A6BFF' : 'rgba(255,255,255,0.06)', color: activo ? 'white' : 'rgba(255,255,255,0.4)', fontSize: 11 }}>
                                {d.slice(0,3)}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* ── HORARIOS: selector por franjas con horas específicas ── */}
                      <div style={{ marginBottom: 12 }}>
                        <label style={styles.editLabel}>Horarios disponibles</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {FRANJAS_HORAS.map(({ id, label, horas }) => {
                            const abierta = franjasAbiertas.includes(id);
                            const horasSeleccionadas = horas.filter(h => (form.horarios || []).includes(h));
                            return (
                              <div key={id}>
                                {/* Cabecera de franja — clic para expandir */}
                                <div onClick={() => toggleFranja(id)}
                                  style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                    padding: '9px 12px', borderRadius: 10, cursor: 'pointer',
                                    border: `1.5px solid ${abierta ? '#1A6BFF' : 'rgba(255,255,255,0.1)'}`,
                                    background: abierta ? 'rgba(26,107,255,0.1)' : 'rgba(255,255,255,0.04)',
                                  }}>
                                  <span style={{
                                    fontSize: 13, fontWeight: 700,
                                    color: abierta ? '#60a5fa' : 'rgba(255,255,255,0.5)',
                                    fontFamily: "'Barlow Condensed', sans-serif",
                                  }}>
                                    {label}
                                  </span>
                                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: "'Barlow', sans-serif" }}>
                                    {horasSeleccionadas.length > 0
                                      ? `${horasSeleccionadas.length} hora${horasSeleccionadas.length > 1 ? 's' : ''} ✓`
                                      : abierta ? '▲' : '▼'}
                                  </span>
                                </div>
                                {/* Horas de la franja */}
                                {abierta && (
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '8px 4px 2px' }}>
                                    {horas.map(hora => {
                                      const activo = (form.horarios || []).includes(hora);
                                      return (
                                        <div key={hora} onClick={() => toggleHora(hora)}
                                          style={{
                                            padding: '6px 14px', borderRadius: 20, cursor: 'pointer',
                                            fontSize: 12, fontWeight: 700, fontFamily: "'Barlow', sans-serif",
                                            background: activo ? '#1A6BFF' : 'rgba(255,255,255,0.06)',
                                            color: activo ? 'white' : 'rgba(255,255,255,0.4)',
                                            border: `1px solid ${activo ? '#1A6BFF' : 'rgba(255,255,255,0.1)'}`,
                                          }}>
                                          {hora}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          {/* Resumen de horas seleccionadas */}
                          {(form.horarios || []).length > 0 && (
                            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow', sans-serif", paddingTop: 2 }}>
                              Seleccionadas: {[...(form.horarios || [])].sort().join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* ── fin horarios ── */}

                    </>
                  ) : (
                    <>
                      {[
                        { key: 'negocio',     label: 'Nombre del negocio' },
                        { key: 'zona',        label: 'Zona'               },
                        { key: 'descripcion', label: 'Descripción'        },
                        { key: 'whatsapp',    label: 'WhatsApp'           },
                        { key: 'instagram',   label: 'Instagram'          },
                        { key: 'facebook',    label: 'Facebook'           },
                        { key: 'tiktok',      label: 'TikTok'             },
                      ].map(({ key, label }) => (
                        <div key={key} style={{ marginBottom: 12 }}>
                          <label style={styles.editLabel}>{label}</label>
                          <input
                            value={form[key] || ''}
                            onChange={e => setForm(p => ({ ...p, [key]: e.target.value }))}
                            style={styles.editInput}
                          />
                        </div>
                      ))}
                      <div style={{ marginBottom: 12 }}>
                        <label style={styles.editLabel}>Servicios ofrecidos</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                          {SERVICIOS_TT.map(({ id, label }) => {
                            const activo = (form.servicios || []).includes(id);
                            return (
                              <div key={id} onClick={() => setForm(p => ({
                                ...p,
                                servicios: activo ? (p.servicios || []).filter(x => x !== id) : [...(p.servicios || []), id]
                              }))}
                              style={{ ...styles.toggleChip, background: activo ? '#FF6B1A' : 'rgba(255,255,255,0.06)', color: activo ? 'white' : 'rgba(255,255,255,0.4)', fontSize: 11 }}>
                                {label}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                  <button
                    style={{ ...styles.btnGuardar, opacity: guardando ? 0.7 : 1 }}
                    onClick={handleGuardar}
                    disabled={guardando}
                  >
                    {guardando ? 'Guardando...' : '✓ Guardar cambios'}
                  </button>
                </div>
              )}
            </div>

            {/* Stats */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>Estadísticas</div>
              <div style={styles.statsGrid}>
                <div style={styles.statBox}>
                  <div style={styles.statNum}>{desafios.length}</div>
                  <div style={styles.statLabel}>Total asignados</div>
                </div>
                <div style={styles.statBox}>
                  <div style={{ ...styles.statNum, color: '#FF6B1A' }}>{pendientes.length}</div>
                  <div style={styles.statLabel}>Próximos</div>
                </div>
                <div style={styles.statBox}>
                  <div style={{ ...styles.statNum, color: '#16A34A' }}>{jugados.length}</div>
                  <div style={styles.statLabel}>Completados</div>
                </div>
              </div>
            </div>

          </div>

          <div style={styles.colRight}>

            {/* ── Partidos disponibles para tomar (solo árbitro) ── */}
            {rol === 'arbitro' && (
              <div style={styles.card}>
                <div style={styles.cardTitle}>
                  🔍 Partidos disponibles
                  {disponibles.length > 0 && (
                    <span style={{ marginLeft: 8, background: '#F59E0B', color: 'white', fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, fontFamily: "'Barlow', sans-serif" }}>
                      {disponibles.length}
                    </span>
                  )}
                </div>
                {disponibles.length === 0 ? (
                  <div style={styles.sinDatos}>No hay partidos disponibles para tu horario ahora.</div>
                ) : (
                  disponibles.map(d => (
                    <div key={d.id} style={{ ...styles.desafioCard, border: '1.5px solid rgba(245,158,11,0.3)', marginBottom: 10 }}>
                      <div style={styles.desafioHeader}>
                        <div style={styles.desafioVs}>
                          {d.local_nombre} vs {d.visitante_nombre}
                        </div>
                        <div style={{ ...styles.estadoBadge, background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
                          Disponible
                        </div>
                      </div>
                      <div style={styles.desafioMeta}>
                        📅 {formatFecha(d.fecha)} · {d.hora}
                        {d.recinto_nombre && ` · 🏟️ ${d.recinto_nombre}`}
                        {(d.cancha_manual || d.cancha_nombre) && ` · ⛳ ${d.cancha_manual || d.cancha_nombre}`}
                      </div>
                      {d.recinto_direccion && (
                        <div
                          style={{ fontSize: 11, color: '#1A6BFF', cursor: 'pointer', fontWeight: 700, fontFamily: "'Barlow', sans-serif", marginTop: 4 }}
                          onClick={() => abrirMapa(d.recinto_direccion, d.recinto_ciudad, d.recinto_nombre)}
                        >
                          📍 Ver ubicación en mapa
                        </div>
                      )}
                      <button
                        onClick={() => handleTomarPartido(d.id)}
                        disabled={tomando === d.id}
                        style={{
                          marginTop: 10, width: '100%', padding: '9px',
                          background: tomando === d.id ? 'rgba(245,158,11,0.4)' : '#F59E0B',
                          border: 'none', borderRadius: 9, cursor: 'pointer',
                          fontFamily: "'Barlow Condensed', sans-serif", fontSize: 14, fontWeight: 800, color: 'white',
                        }}
                      >
                        {tomando === d.id ? 'Tomando...' : '⚡ Tomar este partido'}
                      </button>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Próximos partidos */}
            <div style={styles.card}>
              <div style={styles.cardTitle}>
                {rol === 'arbitro' ? 'Partidos asignados' : 'Eventos contratados'}
              </div>
              {pendientes.length === 0 ? (
                <div style={styles.sinDatos}>
                  No tienes {rol === 'arbitro' ? 'partidos' : 'eventos'} asignados aún.
                </div>
              ) : (
                pendientes.map(d => (
                  <div key={d.id} style={styles.desafioCard}>
                    <div style={styles.desafioHeader}>
                      <div style={styles.desafioVs}>
                        {d.local_nombre} vs {d.visitante_nombre}
                      </div>
                      <div style={{
                        ...styles.estadoBadge,
                        background: d.estado === 'aceptado' ? '#F0FDF4' : '#FFF7ED',
                        color:      d.estado === 'aceptado' ? '#16A34A' : '#FF6B1A',
                      }}>
                        {d.estado === 'aceptado' ? '✓ Confirmado' : '⏳ Pendiente'}
                      </div>
                    </div>
                    <div style={styles.desafioMeta}>
                      📅 {formatFecha(d.fecha)} · {d.hora}
                      {d.recinto_nombre && ` · 🏟️ ${d.recinto_nombre}`}
                      {(d.cancha_manual || d.cancha_nombre) && ` · ⛳ ${d.cancha_manual || d.cancha_nombre}`}
                    </div>
                    {d.recinto_direccion && (
                      <div
                        style={{ fontSize: 11, color: '#1A6BFF', cursor: 'pointer', fontWeight: 700, fontFamily: "'Barlow', sans-serif", marginTop: 4 }}
                        onClick={() => abrirMapa(d.recinto_direccion, d.recinto_ciudad, d.recinto_nombre)}
                      >
                        📍 Ver ubicación en mapa
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Historial */}
            {jugados.length > 0 && (
              <div style={styles.card}>
                <div style={styles.cardTitle}>Historial</div>
                {jugados.slice(0, 5).map(d => (
                  <div key={d.id} style={styles.desafioCard}>
                    <div style={styles.desafioHeader}>
                      <div style={styles.desafioVs}>
                        {d.local_nombre} vs {d.visitante_nombre}
                      </div>
                      {d.goles_local !== null && (
                        <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 800, color: 'white' }}>
                          {d.goles_local} — {d.goles_visitante}
                        </div>
                      )}
                    </div>
                    <div style={styles.desafioMeta}>
                      📅 {formatFecha(d.fecha)} · {d.hora}
                      {d.recinto_nombre && ` · 🏟️ ${d.recinto_nombre}`}
                      {(d.cancha_manual || d.cancha_nombre) && ` · ⛳ ${d.cancha_manual || d.cancha_nombre}`}
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  pagina: { minHeight: '100vh', background: '#0D1117', padding: '32px 20px' },
  container: { maxWidth: 900, margin: '0 auto' },
  cargando: { textAlign: 'center', padding: '80px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#161C26', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 20, padding: '20px 24px', marginBottom: 16 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 16 },
  avatar: { fontSize: 48 },
  nombre: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 24, fontWeight: 800, color: 'white', marginBottom: 4 },
  rolBadge: { background: 'rgba(255,107,26,0.2)', color: '#FF6B1A', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, fontFamily: "'Barlow', sans-serif", display: 'inline-block', marginBottom: 4 },
  email: { fontSize: 12, color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow', sans-serif" },
  headerRight: { display: 'flex', gap: 8 },
  btnEditar: { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 10, padding: '8px 16px', color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },
  btnSalir: { background: 'transparent', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '8px 16px', color: '#EF4444', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Barlow', sans-serif" },
  notifBanner: { display: 'flex', alignItems: 'center', gap: 14, background: 'rgba(255,107,26,0.1)', border: '1px solid rgba(255,107,26,0.3)', borderRadius: 14, padding: '14px 18px', marginBottom: 16 },
  notifIcono: { fontSize: 28, flexShrink: 0 },
  notifTitulo: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 16, fontWeight: 800, color: '#FF6B1A', marginBottom: 2 },
  notifSub: { fontSize: 12, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" },
  errorBanner: { background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '10px 16px', fontSize: 13, color: '#EF4444', fontFamily: "'Barlow', sans-serif", marginBottom: 16 },
  cols: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, alignItems: 'start' },
  colLeft: { display: 'flex', flexDirection: 'column', gap: 16 },
  colRight: { display: 'flex', flexDirection: 'column', gap: 16 },
  card: { background: '#161C26', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '18px' },
  cardTitle: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 14 },
  infoRow: { display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  infoLabel: { fontSize: 12, color: 'rgba(255,255,255,0.3)', fontFamily: "'Barlow', sans-serif" },
  infoVal: { fontSize: 12, color: 'white', fontFamily: "'Barlow', sans-serif", fontWeight: 600, textAlign: 'right', maxWidth: '60%' },
  editLabel: { display: 'block', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, fontFamily: "'Barlow', sans-serif" },
  editInput: { width: '100%', padding: '10px 12px', background: 'rgba(255,255,255,0.06)', border: '1.5px solid rgba(255,255,255,0.1)', borderRadius: 9, fontSize: 13, color: 'white', fontFamily: "'Barlow', sans-serif", outline: 'none', boxSizing: 'border-box' },
  chipVista: { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 20, fontFamily: "'Barlow', sans-serif" },
  toggleChip: { padding: '7px 16px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: "'Barlow', sans-serif" },
  btnGuardar: { width: '100%', padding: '11px', background: '#FF6B1A', border: 'none', borderRadius: 10, fontFamily: "'Barlow Condensed', sans-serif", fontSize: 15, fontWeight: 800, color: 'white', cursor: 'pointer', marginTop: 8 },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 },
  statBox: { background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '12px', textAlign: 'center' },
  statNum: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 28, fontWeight: 800, color: 'white', lineHeight: 1, marginBottom: 4 },
  statLabel: { fontSize: 10, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: "'Barlow', sans-serif" },
  sinDatos: { fontSize: 13, color: 'rgba(255,255,255,0.25)', fontFamily: "'Barlow', sans-serif", fontStyle: 'italic' },
  desafioCard: { background: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: '12px', marginBottom: 8 },
  desafioHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 },
  desafioVs: { fontFamily: "'Barlow Condensed', sans-serif", fontSize: 15, fontWeight: 700, color: 'white' },
  desafioMeta: { fontSize: 11, color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow', sans-serif" },
  estadoBadge: { fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20, fontFamily: "'Barlow', sans-serif" },
};